import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Send, MessageSquare, ArrowLeft, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/**
 * Threaded messaging between admin and the other roles (kunde / service / vask).
 *
 * Admin view: list of threads on the left (one per kunde/service/vask user),
 * conversation pane on the right.
 * Non-admin view: a single conversation with admin.
 */
export default function MessagesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [threads, setThreads] = useState([]); // admin only
  const [activeThread, setActiveThread] = useState(null); // {user, thread_user_id}
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  // Load thread list (admin) or single thread (non-admin)
  const loadThreads = () => {
    if (!isAdmin) {
      api.get("/messages").then((r) => {
        setMessages(r.data);
        api.post("/messages/mark-read", {}).catch(() => {});
      });
      return;
    }
    api.get("/messages").then((r) => setThreads(r.data));
  };

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 15000); // poll every 15s
    return () => clearInterval(t);
  }, []);

  const openThread = (thr) => {
    setActiveThread(thr);
    api.get(`/messages?thread_user_id=${thr.thread_user_id}`).then((r) => {
      setMessages(r.data);
      api.post("/messages/mark-read", { thread_user_id: thr.thread_user_id })
        .then(loadThreads)
        .catch(() => {});
    });
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const payload = { body: draft.trim() };
      if (isAdmin) {
        if (!activeThread) {
          toast.error("Velg en tråd først");
          setSending(false);
          return;
        }
        payload.to_user_id = activeThread.thread_user_id;
      }
      await api.post("/messages", payload);
      setDraft("");
      if (isAdmin && activeThread) {
        const r = await api.get(`/messages?thread_user_id=${activeThread.thread_user_id}`);
        setMessages(r.data);
      } else {
        const r = await api.get("/messages");
        setMessages(r.data);
      }
      loadThreads();
    } catch {
      toast.error("Kunne ikke sende");
    } finally {
      setSending(false);
    }
  };

  const me = user?.id;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Kommunikasjon</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Meldinger</h1>
          <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
            {isAdmin
              ? "Direkte meldinger til/fra kunder, service og vask. Bruk «Ny melding» for å sende oppdateringer eller kringkaste."
              : "Send melding direkte til admin. Du får varsel når noen svarer."}
          </p>
        </div>
        {isAdmin && <NewMessageButton onSent={loadThreads} />}
      </div>

      <div className={`grid gap-4 ${isAdmin ? "lg:grid-cols-[320px_1fr]" : ""}`}>
        {isAdmin && (
          <Card className="p-2 max-h-[70vh] overflow-y-auto" data-testid="msg-thread-list">
            {threads.length === 0 && (
              <p className="p-4 text-sm text-[#2B3F4D]">Ingen meldinger ennå.</p>
            )}
            {threads.map((t) => {
              const isActive = activeThread?.thread_user_id === t.thread_user_id;
              return (
                <button
                  key={t.thread_user_id}
                  type="button"
                  onClick={() => openThread(t)}
                  data-testid={`msg-thread-${t.thread_user_id}`}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                    isActive ? "bg-[#0C1824] text-white" : "hover:bg-slate-100"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">
                      {t.user?.name || "(slettet)"}
                    </div>
                    {t.unread_count > 0 && (
                      <Badge className="bg-[#BD984D] text-white border-0 flex-shrink-0">
                        {t.unread_count}
                      </Badge>
                    )}
                  </div>
                  <div className={`text-xs mt-0.5 ${isActive ? "text-white/70" : "text-[#A57F39]"}`}>
                    {t.user?.role}
                  </div>
                  <div className={`text-xs mt-1 truncate ${isActive ? "text-white/80" : "text-[#2B3F4D]"}`}>
                    {t.last_message.body}
                  </div>
                </button>
              );
            })}
          </Card>
        )}

        <Card className="flex flex-col h-[70vh]" data-testid="msg-conversation">
          {isAdmin && !activeThread ? (
            <div className="flex-1 flex items-center justify-center text-[#2B3F4D] text-sm">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-[#BD984D]" />
                Velg en tråd til venstre for å åpne samtalen.
              </div>
            </div>
          ) : (
            <>
              {isAdmin && activeThread && (
                <div className="p-3 border-b border-[#E2E8F0] flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setActiveThread(null); setMessages([]); }}
                    className="lg:hidden p-1 hover:bg-slate-100 rounded"
                    aria-label="Tilbake"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  <div>
                    <div className="font-semibold text-[#0C1824]">{activeThread.user?.name}</div>
                    <div className="text-xs text-[#A57F39]">{activeThread.user?.role} · {activeThread.user?.email}</div>
                  </div>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="msg-thread">
                {messages.length === 0 && (
                  <p className="text-sm text-[#2B3F4D] text-center py-8">
                    Skriv den første meldingen i denne tråden.
                  </p>
                )}
                {messages.map((m) => {
                  const mine = m.from_user_id === me;
                  return (
                    <div
                      key={m.id}
                      data-testid={`msg-${m.id}`}
                      className={`flex ${mine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                          mine
                            ? "bg-[#0C1824] text-white rounded-br-sm"
                            : "bg-[#BD984D]/15 text-[#0C1824] rounded-bl-sm"
                        }`}
                      >
                        <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${
                          mine ? "text-white/70" : "text-[#A57F39]"
                        }`}>
                          {m.from_name} · {m.from_role}
                        </div>
                        <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#2B3F4D]"}`}>
                          {new Date(m.created_at).toLocaleString("nb-NO", {
                            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-[#E2E8F0] flex gap-2">
                <Textarea
                  rows={2}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={isAdmin ? "Skriv svar…" : "Skriv til admin…"}
                  data-testid="msg-input"
                  className="resize-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                  }}
                />
                <Button
                  onClick={send}
                  disabled={sending || !draft.trim()}
                  data-testid="msg-send"
                  className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white self-end h-11 px-4"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}


function NewMessageButton({ onSent }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("all");
  const [userId, setUserId] = useState("");
  const [body, setBody] = useState("");
  const [staff, setStaff] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => setStaff(r.data)).catch(() => {});
  }, [open]);

  const send = async () => {
    if (!body.trim()) { toast.error("Skriv en melding"); return; }
    if (target === "user" && !userId) { toast.error("Velg mottaker"); return; }
    setSending(true);
    try {
      const payload = { body: body.trim() };
      if (target === "user") payload.to_user_id = userId;
      else if (target === "all") payload.to_all = true;
      else if (target === "staff") payload.to_staff = true;
      else payload.to_role = target; // 'vask' | 'service' | 'kunde'
      const { data } = await api.post("/messages", payload);
      const count = typeof data?.sent === "number" ? data.sent : 1;
      toast.success(`Sendt til ${count} mottaker(e)`);
      setOpen(false); setBody(""); setUserId(""); setTarget("all");
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="msg-new-btn" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Ny melding
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send ny melding</DialogTitle>
          <DialogDescription>
            Send oppdatering eller beskjed til en bestemt bruker, en hel rolle, eller alle.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Mottaker</Label>
            <Select value={target} onValueChange={(v) => { setTarget(v); setUserId(""); }}>
              <SelectTrigger data-testid="msg-new-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle brukere (inkl. admin og kunder)</SelectItem>
                <SelectItem value="staff">Alle ansatte (vask + service)</SelectItem>
                <SelectItem value="vask">Alle med rolle: Vask</SelectItem>
                <SelectItem value="service">Alle med rolle: Service</SelectItem>
                <SelectItem value="kunde">Alle aktive kunder</SelectItem>
                <SelectItem value="user">Bestemt bruker …</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {target === "user" && (
            <div>
              <Label>Velg bruker</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="msg-new-user"><SelectValue placeholder="Velg …" /></SelectTrigger>
                <SelectContent>
                  {staff.filter((u) => u.role !== "admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Melding</Label>
            <Textarea
              rows={5} value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Skriv beskjeden …"
              data-testid="msg-new-body"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Avbryt</Button>
          <Button onClick={send} disabled={sending} data-testid="msg-new-send"
                  className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
            <Send className="w-4 h-4 mr-1.5" /> {sending ? "Sender…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
